import { useEffect, useState } from 'react';
import api from '../api/axios';
import './UpcomingFollowUps.css';

function UpcomingFollowUps() {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterDueDate, setFilterDueDate] = useState('');
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchUpcomingFollowUps();
    fetchEmployees();
    // Refresh every 5 minutes
    const interval = setInterval(fetchUpcomingFollowUps, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchUpcomingFollowUps = async () => {
    try {
      console.log('Fetching upcoming follow-ups...');
      const response = await api.get('/lead-assignments/upcoming/list');
      console.log('Upcoming follow-ups:', response.data);
      setFollowUps(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      const salesEmps = response.data.filter(emp => emp.role?.name === 'Sales');
      setEmployees(salesEmps);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Filter follow-ups
  const filteredFollowUps = followUps.filter((lead) => {
    const employeeMatch = !filterEmployee || lead.employee?._id === filterEmployee;
    let dueDateMatch = true;
    
    if (filterDueDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const followUpDate = new Date(lead.nextFollowUp);
      followUpDate.setHours(0, 0, 0, 0);
      
      if (filterDueDate === 'today') {
        dueDateMatch = followUpDate.getTime() === today.getTime();
      } else if (filterDueDate === 'week') {
        const weekAhead = new Date(today);
        weekAhead.setDate(weekAhead.getDate() + 7);
        dueDateMatch = followUpDate <= weekAhead && followUpDate >= today;
      } else if (filterDueDate === 'overdue') {
        dueDateMatch = followUpDate < today;
      }
    }
    
    return employeeMatch && dueDateMatch;
  });

  const getDaysDifference = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays <= 7) return `In ${diffDays} days`;
    return `In ${diffDays} days`;
  };

  const getUrgencyColor = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    
    const diffDays = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return '#F44336'; // Red - overdue
    if (diffDays <= 1) return '#FF9800'; // Orange - urgent
    if (diffDays <= 3) return '#FFC107'; // Yellow - soon
    return '#4CAF50'; // Green - upcoming
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading follow-ups...</p>
      </div>
    );
  }

  return (
    <div className="upcoming-follow-ups">
      <h2 style={{ marginBottom: '20px' }}>📞 Upcoming Follow-ups</h2>
      
      {/* Filters */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '25px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
            Filter by Employee
          </label>
          <select
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '10px', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="">All Employees</option>
            {employees.map((emp) => (
              <option key={emp._id} value={emp._id}>
                {emp.firstName} {emp.lastName} ({emp.employeeId})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
            Filter by Due Date
          </label>
          <select
            value={filterDueDate}
            onChange={(e) => setFilterDueDate(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '10px', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="">All Dates</option>
            <option value="overdue">Overdue</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
          </select>
        </div>
      </div>

      {/* Follow-ups List */}
      {filteredFollowUps.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px' }}>
          {filteredFollowUps.map((lead) => (
            <div
              key={lead._id}
              style={{
                border: `3px solid ${getUrgencyColor(lead.nextFollowUp)}`,
                borderRadius: '8px',
                padding: '20px',
                backgroundColor: '#fff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              {/* Urgency Badge */}
              <div style={{ marginBottom: '12px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    backgroundColor: getUrgencyColor(lead.nextFollowUp),
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  {getDaysDifference(lead.nextFollowUp)}
                </span>
              </div>

              {/* Lead Details */}
              <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '18px' }}>
                {lead.leadName}
              </h3>

              <div style={{ marginBottom: '12px', fontSize: '13px', color: '#666' }}>
                <p style={{ margin: '5px 0' }}>
                  <strong>👤 Assigned to:</strong> {lead.employee?.firstName} {lead.employee?.lastName} ({lead.employee?.employeeId})
                </p>
                <p style={{ margin: '5px 0' }}>
                  <strong>📞 Contact:</strong> {lead.contactNumber}
                </p>
                <p style={{ margin: '5px 0' }}>
                  <strong>🏢 Company:</strong> {lead.company || '-'}
                </p>
                <p style={{ margin: '5px 0' }}>
                  <strong>📅 Follow-up Date:</strong> {new Date(lead.nextFollowUp).toLocaleDateString('en-IN', { 
                    weekday: 'short', 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </p>
              </div>

              {/* Follow-up Notes */}
              {lead.followUpNotes && (
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: '10px',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  fontSize: '13px',
                  color: '#555',
                  borderLeft: '4px solid #2196F3'
                }}>
                  <strong>📝 Follow-up Plan:</strong>
                  <p style={{ margin: '5px 0 0 0', whiteSpace: 'pre-wrap' }}>
                    {lead.followUpNotes}
                  </p>
                </div>
              )}

              {/* Lead Status */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '10px',
                borderTop: '1px solid #eee'
              }}>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  backgroundColor: '#e3f2fd',
                  color: '#1976D2',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  Status: {lead.status.toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <p>✅ No upcoming follow-ups!</p>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{ marginTop: '40px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
        <div style={{ padding: '15px', backgroundColor: '#F44336', borderRadius: '8px', textAlign: 'center', color: 'white' }}>
          <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '0' }}>
            {followUps.filter(l => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const followUpDate = new Date(l.nextFollowUp);
              followUpDate.setHours(0, 0, 0, 0);
              return followUpDate < today;
            }).length}
          </p>
          <p style={{ fontSize: '12px', margin: '5px 0 0 0' }}>Overdue</p>
        </div>

        <div style={{ padding: '15px', backgroundColor: '#FF9800', borderRadius: '8px', textAlign: 'center', color: 'white' }}>
          <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '0' }}>
            {followUps.filter(l => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const followUpDate = new Date(l.nextFollowUp);
              followUpDate.setHours(0, 0, 0, 0);
              const diffDays = Math.ceil((followUpDate - today) / (1000 * 60 * 60 * 24));
              return diffDays >= 0 && diffDays <= 1;
            }).length}
          </p>
          <p style={{ fontSize: '12px', margin: '5px 0 0 0' }}>Due Today/Tomorrow</p>
        </div>

        <div style={{ padding: '15px', backgroundColor: '#2196F3', borderRadius: '8px', textAlign: 'center', color: 'white' }}>
          <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '0' }}>
            {followUps.length}
          </p>
          <p style={{ fontSize: '12px', margin: '5px 0 0 0' }}>Total Follow-ups</p>
        </div>
      </div>
    </div>
  );
}

export default UpcomingFollowUps;
